import React, { useState, useEffect, useRef, useCallback } from 'react';
import './RangeSlider.css';

interface RangeSliderProps {
  min?: number;
  max?: number;
  value?: { min: number; max: number } | null;
  onChange: (value: { min: number; max: number }) => void;
  step?: number;
  className?: string;
  showTickMarks?: boolean;
  tickStep?: number;
  formatValue?: (value: number) => string;
}

const RangeSlider: React.FC<RangeSliderProps> = ({
  min = 0,
  max = 100,
  value,
  onChange,
  step = 1,
  className = '',
  showTickMarks = false,
  tickStep = 5,
  formatValue = (val) => val.toString()
}) => {
  // Set better default values for budget ranges
  const defaultMin = min === 0 && max >= 150000 ? 10000 : min;
  const defaultMax = min === 0 && max >= 150000 ? 150000 : max;
  
  const [minValue, setMinValue] = useState(value?.min ?? defaultMin);
  const [maxValue, setMaxValue] = useState(value?.max ?? defaultMax);
  const [activeThumb, setActiveThumb] = useState<'min' | 'max' | null>(null);
  const range = useRef<HTMLDivElement>(null);
  const isUpdatingRef = useRef(false);

  // Initialize parent value if it's null
  useEffect(() => {
    if (!value) {
      onChange({ min: minValue, max: maxValue });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Convert to percentage
  const getPercent = useCallback((value: number) => Math.round(((value - min) / (max - min)) * 100), [min, max]);

  // Update the range fill when values change
  useEffect(() => {
    if (range.current) {
      const minPercent = getPercent(minValue);
      const maxPercent = getPercent(maxValue);
      
      range.current.style.left = `${minPercent}%`;
      range.current.style.width = `${maxPercent - minPercent}%`;
    }
  }, [minValue, maxValue, getPercent]);

  // Update internal state when prop value changes (only if different and not currently updating)
  useEffect(() => {
    if (!isUpdatingRef.current && value && (value.min !== minValue || value.max !== maxValue)) {
      setMinValue(value.min);
      setMaxValue(value.max);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value?.min, value?.max]); // Intentionally exclude minValue/maxValue to prevent loops

  const handleMinChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = Math.min(Number(event.target.value), maxValue - step);
    isUpdatingRef.current = true;
    setMinValue(newValue);
    onChange({ min: newValue, max: maxValue });
    setTimeout(() => { isUpdatingRef.current = false; }, 0);
  }, [maxValue, step, onChange]);

  const handleMaxChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = Math.max(Number(event.target.value), minValue + step);
    isUpdatingRef.current = true;
    setMaxValue(newValue);
    onChange({ min: minValue, max: newValue });
    setTimeout(() => { isUpdatingRef.current = false; }, 0);
  }, [minValue, step, onChange]);

  const handleMinFocus = () => setActiveThumb('min');
  const handleMaxFocus = () => setActiveThumb('max');
  const handleBlur = () => setActiveThumb(null);



  // Generate tick marks
  const generateTickMarks = () => {
    if (!showTickMarks) return [];
    
    const ticks = [];
    // Generate fewer ticks for better visibility - every 10k with major ticks every 25k
    const actualTickStep = Math.max(tickStep, 10000);
    for (let i = min; i <= max; i += actualTickStep) {
      const percent = ((i - min) / (max - min)) * 100;
      ticks.push({
        value: i,
        percent,
        isMainTick: i % 25000 === 0 // Major ticks every 25k
      });
    }
    return ticks;
  };

  const tickMarks = generateTickMarks();

  return (
    <div className={`relative ${className}`}>
      {/* Range track */}
      <div className="relative h-2 bg-gray-200 rounded-lg mt-4">
        {/* Range fill */}
        <div
          ref={range}
          className="absolute h-2 bg-blue-500 rounded-lg"
        />
        
        {/* Tick marks */}
        {tickMarks.map((tick) => (
          <div
            key={tick.value}
            className={`absolute ${
              tick.isMainTick 
                ? 'w-0.5 h-8 -top-3 bg-gray-600' 
                : 'w-px h-4 -top-1 bg-gray-400'
            }`}
            style={{ left: `${tick.percent}%` }}
          />
        ))}
      </div>
      
      {/* Tick mark labels */}
      {showTickMarks && (
        <div className="relative mt-6">
          {tickMarks
            .filter(tick => tick.isMainTick)
            .map((tick) => (
              <div
                key={tick.value}
                className="absolute text-xs text-gray-600 transform -translate-x-1/2 font-medium"
                style={{ left: `${tick.percent}%` }}
              >
                {formatValue(tick.value)}
              </div>
            ))}
        </div>
      )}
      
      {/* Input sliders */}
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={minValue}
        onChange={handleMinChange}
        onFocus={handleMinFocus}
        onBlur={handleBlur}
        className="absolute w-full h-2 bg-transparent appearance-none cursor-pointer range-slider-thumb"
        style={{ 
          zIndex: activeThumb === 'min' ? 5 : (minValue > maxValue - (max - min) * 0.05 ? 4 : 3), 
          top: '1rem' 
        }}
      />
      
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={maxValue}
        onChange={handleMaxChange}
        onFocus={handleMaxFocus}
        onBlur={handleBlur}
        className="absolute w-full h-2 bg-transparent appearance-none cursor-pointer range-slider-thumb"
        style={{ 
          zIndex: activeThumb === 'max' ? 5 : 4, 
          top: '1rem' 
        }}
      />
      
      {/* Value display */}
      <div className="flex justify-between mt-8 text-sm font-medium text-gray-700">
        <div className="text-center">
          <div className="text-gray-500 text-xs">Min</div>
          <div className="font-semibold">{formatValue(minValue)}</div>
        </div>
        <div className="text-center">
          <div className="text-gray-500 text-xs">Max</div>
          <div className="font-semibold">{formatValue(maxValue)}</div>
        </div>
      </div>
    </div>
  );
};

export default RangeSlider;